import React, { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import Button from "../../../Button";
import ModalBalcaoVirtual from "../../ModalBalcaoVirtual";

import slideWelcome from "../../../../assets/images/landingPage/v2/home-hero.webp";
import slideVirtualDesk from "../../../../assets/images/landingPage/v2/home-slide2.webp";
import slideProtest from "../../../../assets/images/landingPage/v2/home-slide3.webp";
import slideDebts from "../../../../assets/images/landingPage/v2/home-slide4.webp";

import style from "./styles.module.scss";

const keyI18n = "translations.home.slideshow";

const IMAGE_WIDTH = 600;
const IMAGE_HEIGHT = 464;

interface SlideAction {
  labelKey: string;
  variant: "filled" | "outlined";
  icon: string;
  iconPosition: "leading" | "trailing";
  href?: string;
  target?: "_blank";
  action?: "virtualDesk";
}

interface Slide {
  name: string;
  image: string;
  actions: SlideAction[];
}

const slides: Slide[] = [
  {
    name: "welcome",
    image: slideWelcome,
    actions: [
      {
        labelKey: `${keyI18n}.welcome.actions.negotiate`,
        variant: "filled",
        icon: "open_in_new",
        iconPosition: "leading",
        href: "#servicos-online",
      },
      {
        labelKey: `${keyI18n}.welcome.actions.guide`,
        variant: "outlined",
        icon: "download",
        iconPosition: "leading",
        href: "/Guia_-_Portal_PGM.pdf",
        target: "_blank",
      },
    ],
  },
  {
    name: "virtualDesk",
    image: slideVirtualDesk,
    actions: [
      {
        labelKey: `${keyI18n}.virtualDesk.actions.open`,
        variant: "filled",
        icon: "arrow_outward",
        iconPosition: "trailing",
        action: "virtualDesk",
      },
      {
        labelKey: `${keyI18n}.virtualDesk.actions.details`,
        variant: "outlined",
        icon: "arrow_forward",
        iconPosition: "trailing",
        href: "/atendimento_sem_sair_de_casa",
      },
    ],
  },
  {
    name: "protest",
    image: slideProtest,
    actions: [
      {
        labelKey: `${keyI18n}.protest.actions.support`,
        variant: "filled",
        icon: "open_in_new",
        iconPosition: "leading",
        href:
          "https://api.whatsapp.com/send/?phone=05585982009501&text=Ol%C3%A1+eu+desejo+suporte+sobre+Protesto&type=phone_number&app_absent=0",
        target: "_blank",
      },
    ],
  },
  {
    name: "debts",
    image: slideDebts,
    actions: [
      {
        labelKey: `${keyI18n}.debts.actions.negotiate`,
        variant: "filled",
        icon: "arrow_forward",
        iconPosition: "trailing",
        href: "/consultar/debitos/em_aberto",
      },
      {
        labelKey: `${keyI18n}.debts.actions.guide`,
        variant: "outlined",
        icon: "open_in_new",
        iconPosition: "trailing",
        href: "/Guia_-_Portal_PGM.pdf",
        target: "_blank",
      },
    ],
  },
];

export default function Slideshow() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const [showVirtualDesk, setShowVirtualDesk] = useState(false);

  const goTo = useCallback((index: number) => {
    setCurrent((index + slides.length) % slides.length);
  }, []);


  return (
    <>
      <section
        aria-label={t(`${keyI18n}.aria.region`)}
        aria-roledescription="carousel"
        className={style.slideshow}
      >
        <span aria-hidden="true" className={style.mosaicLeft} />
        <span aria-hidden="true" className={style.mosaicRight} />

        <div className={style.container}>
          <Button
            aria-label={t(`${keyI18n}.aria.previous`)}
            className={style.control}
            icon={<span className="material-symbols-sharp">arrow_back</span>}
            iconButton
            onClick={() => goTo(current - 1)}
            tooltip={t(`${keyI18n}.controls.previous`)}
            variant="tonal"
          />

          <div className={style.viewport}>
            <div
              className={style.track}
              style={{ transform: `translateX(-${current * 100}%)` }}
            >
              {slides.map((slide, index) => {
                const isActive = index === current;

                return (
                  <div
                    aria-hidden={!isActive}
                    className={style.slide}
                    key={slide.name}
                    {...(isActive ? {} : ({ inert: "" } as any))}
                  >
                    <div className={style.infos}>
                      <div className={style.text}>
                        <h1
                          className={`pgm-display-medium pgm-display-medium--emphasized ${style.title}`}
                        >
                          {t(`${keyI18n}.${slide.name}.title`)}
                        </h1>
                        <p className={`pgm-title-large ${style.description}`}>
                          <Trans
                            i18nKey={`${keyI18n}.${slide.name}.description`}
                            components={{ b: <strong /> }}
                          />
                        </p>
                      </div>

                      <div className={style.actions}>
                        {slide.actions.map(
                          ({ labelKey, variant, icon, iconPosition, href, target, action }) => {
                            const iconElement = (
                              <span className="material-symbols-sharp">{icon}</span>
                            );

                            return (
                              <Button
                                href={href}
                                icon={iconPosition === "leading" ? iconElement : undefined}
                                key={labelKey}
                                onClick={
                                  action === "virtualDesk"
                                    ? () => setShowVirtualDesk(true)
                                    : undefined
                                }
                                size="medium"
                                target={target}
                                trailingIcon={
                                  iconPosition === "trailing" ? iconElement : undefined
                                }
                                variant={variant}
                              >
                                {t(labelKey)}
                              </Button>
                            );
                          },
                        )}
                      </div>
                    </div>

                    <img
                      alt={t(`${keyI18n}.${slide.name}.imageAlt`)}
                      className={style.image}
                      decoding="async"
                      height={IMAGE_HEIGHT}
                      src={slide.image}
                      width={IMAGE_WIDTH}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <Button
            aria-label={t(`${keyI18n}.aria.next`)}
            className={style.control}
            icon={<span className="material-symbols-sharp">arrow_forward</span>}
            iconButton
            onClick={() => goTo(current + 1)}
            tooltip={t(`${keyI18n}.controls.next`)}
            variant="tonal"
          />
        </div>

        <div className={style.steps} role="tablist">
          {slides.map(({ name }, index) => (
            <button
              aria-label={t(`${keyI18n}.${name}.title`)}
              aria-selected={index === current}
              className={index === current ? style.stepActive : style.step}
              key={name}
              onClick={() => goTo(index)}
              role="tab"
              type="button"
            />
          ))}
        </div>
      </section>

      <ModalBalcaoVirtual
        onHide={() => setShowVirtualDesk(false)}
        show={showVirtualDesk}
      />
    </>
  );
}
